import { PropsWithChildren, ReactNode } from 'react';
import dynamic from 'next/dynamic';

import { Box, Flex, FlexProps } from '@chakra-ui/react';

import { publicEnv } from '~/features/environment/public';
import { MaintenanceBanner } from '~/features/maintenance/components/maintenance-banner';
import { PageHeader } from '~/features/page-header/components/page-header';
import { PromotionBanner } from '~/features/promotions/components/promotion-banner';
import { useUserExperience } from '~/features/user/hooks/use-user-experience';
import { useClientRender } from '~/hooks/use-client-render';

import { PageFooter } from '../../page-footer/components/page-footer';

const UpdatedTermsOfServiceBanner = dynamic<{}>(
  () =>
    import('~/features/legal/components/updated-terms-of-service-banner').then(
      (mod) => mod.UpdatedTermsOfServiceBanner
    ),
  {
    ssr: false,
  }
);

export function PageLayout({
  withoutVerticalPadding,
  isSimpleMode,
  children,
  bannerSlot = <PromotionBanner />,
  termsOfServiceBannerSlot = <UpdatedTermsOfServiceBanner />,
  isPageHeaderHidden = false,
  bg,
  ...flexProps
}: PropsWithChildren<
  {
    withoutVerticalPadding?: boolean;
    isSimpleMode?: boolean;
    bannerSlot?: ReactNode;
    termsOfServiceBannerSlot?: ReactNode;
    isPageHeaderHidden?: boolean;
  } & FlexProps
>) {
  const clientReady = useClientRender();
  const { userExperience } = useUserExperience();

  return (
    <Flex flexDirection='column' height='100%' {...flexProps}>
      <PageHeader isHidden={isPageHeaderHidden} isSimpleMode={isSimpleMode} />
      {publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'test' && clientReady && (
        <>
          <MaintenanceBanner />
          {bannerSlot}
        </>
      )}
      <Box as='main' bg={bg} flex={1} py={withoutVerticalPadding ? undefined : { base: '24px', md: '40px' }}>
        {children}
      </Box>
      {!isSimpleMode && !userExperience?.hasFocused && (
        <>
          <Box h='64px' />
          <PageFooter />
        </>
      )}
      {termsOfServiceBannerSlot}
    </Flex>
  );
}
